import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { sessionTypesAPI } from "../../services/api";
import { useTranslation } from "react-i18next";

const MembershipPackageModal = ({
  isOpen,
  onClose,
  onSubmit,
  package: membershipPackage,
  title,
  organizationId,
  initialSessionTypeId = "",
  initialSessionTypeName = "",
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    numberOfSessions: "",
    price: "",
    validityPeriod: "30",
    allowedFreeCancellations: "0",
    cancellationDeadlineHours: "24",
    lateCancellationPolicy: "deduct",
    noShowPolicy: "deduct",
    sessionTypeId: "",
    sessionTypeName: "",
    maxParticipants: 1,
    status: "active",
    description: "",
    features: [],
    isPopular: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [newFeature, setNewFeature] = useState("");
  const [sessionTypes, setSessionTypes] = useState([]);
  const [loadingSessionTypes, setLoadingSessionTypes] = useState(false);

  useEffect(() => {
    const fetchSessionTypes = async () => {
      if (organizationId && isOpen) {
        setLoadingSessionTypes(true);
        try {
          const response = await sessionTypesAPI.getAllByOrg(organizationId);
          setSessionTypes(response.data.data || []);
        } catch (error) {
          console.error("Error fetching session types:", error);
        } finally {
          setLoadingSessionTypes(false);
        }
      }
    };
    fetchSessionTypes();
  }, [organizationId, isOpen]);

  useEffect(() => {
    if (membershipPackage) {
      setFormData({
        name: membershipPackage.name || "",
        numberOfSessions: membershipPackage.numberOfSessions || "",
        price: membershipPackage.price || "",
        validityPeriod: membershipPackage.validityPeriod || "30",
        allowedFreeCancellations:
          membershipPackage.allowedFreeCancellations ?? "0",
        cancellationDeadlineHours:
          membershipPackage.cancellationDeadlineHours ?? "24",
        lateCancellationPolicy:
          membershipPackage.lateCancellationPolicy || "deduct",
        noShowPolicy: membershipPackage.noShowPolicy || "deduct",
        sessionTypeId: membershipPackage.sessionTypeId || "",
        sessionTypeName: membershipPackage.sessionTypeName || "",
        maxParticipants: membershipPackage.maxParticipants || 1,
        status: membershipPackage.status || "active",
        description: membershipPackage.description || "",
        features: membershipPackage.features || [],
        isPopular: membershipPackage.isPopular || false,
      });
    } else {
      setFormData({
        name: "",
        numberOfSessions: "",
        price: "",
        validityPeriod: "30",
        allowedFreeCancellations: "0",
        cancellationDeadlineHours: "24",
        lateCancellationPolicy: "deduct",
        noShowPolicy: "deduct",
        sessionTypeId: initialSessionTypeId || "",
        sessionTypeName: initialSessionTypeName || "",
        maxParticipants: 1,
        status: "active",
        description: "",
        features: [],
        isPopular: false,
      });
    }
    setErrors({});
    setNewFeature("");
  }, [membershipPackage, isOpen, initialSessionTypeId, initialSessionTypeName]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.sessionTypeId) {
      newErrors.sessionTypeId = t("membershipPackageModal.errors.typeRequired");
    }

    if (!formData.name.trim()) {
      newErrors.name = t("membershipPackageModal.errors.nameRequired");
    }

    if (
      !formData.numberOfSessions ||
      parseInt(formData.numberOfSessions) <= 0
    ) {
      newErrors.numberOfSessions = t(
        "membershipPackageModal.errors.sessionsPositive",
      );
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = t("membershipPackageModal.errors.pricePositive");
    }

    if (formData.validityPeriod && parseInt(formData.validityPeriod) <= 0) {
      newErrors.validityPeriod = t(
        "membershipPackageModal.errors.validityPositive",
      );
    }

    if (
      formData.allowedFreeCancellations &&
      parseInt(formData.allowedFreeCancellations) < 0
    ) {
      newErrors.allowedFreeCancellations = t(
        "membershipPackageModal.errors.cancellationNonNegative",
      );
    }

    if (
      formData.cancellationDeadlineHours &&
      parseInt(formData.cancellationDeadlineHours) < 0
    ) {
      newErrors.cancellationDeadlineHours = t(
        "membershipPackageModal.errors.deadlineNonNegative",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSessionTypeChange = (e) => {
    const selectedTypeId = e.target.value;
    const selectedType = sessionTypes.find((t) => t.id === selectedTypeId);

    setFormData((prev) => ({
      ...prev,
      sessionTypeId: selectedTypeId,
      sessionTypeName: selectedType ? selectedType.name : "",
      maxParticipants: selectedType ? selectedType.maxParticipants : 1,
    }));

    if (errors.sessionTypeId) {
      setErrors((prev) => ({ ...prev, sessionTypeId: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        numberOfSessions: parseInt(formData.numberOfSessions),
        price: parseFloat(formData.price),
        validityPeriod: parseInt(formData.validityPeriod),
        allowedFreeCancellations: parseInt(formData.allowedFreeCancellations),
        cancellationDeadlineHours: parseInt(formData.cancellationDeadlineHours),
        lateCancellationPolicy: formData.lateCancellationPolicy,
        noShowPolicy: formData.noShowPolicy,
        sessionTypeId: formData.sessionTypeId,
        sessionTypeName: formData.sessionTypeName,
        maxParticipants: formData.maxParticipants,
      };
      await onSubmit(submitData);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const addFeature = () => {
    if (newFeature.trim() && !formData.features.includes(newFeature.trim())) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, newFeature.trim()],
      }));
      setNewFeature("");
    }
  };

  const removeFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const calculateSingleSessionPrice = () => {
    const price = parseFloat(formData.price) || 0;
    const sessions = parseInt(formData.numberOfSessions) || 1;
    return sessions > 0 ? (price / sessions).toFixed(2) : "0.00";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`${colors.cardBg} backdrop-blur-sm rounded-lg shadow-xl border ${colors.cardBorder} w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden`}
      >
        {/* Header */}
        <div className="modal-header">
          <h2 className={`text-lg font-semibold ${colors.textPrimary}`}>
            {title}
          </h2>
          <button
            onClick={onClose}
            className={`p-1 ${colors.bgHover} rounded transition-colors`}
          >
            <X className={`w-5 h-5 ${colors.textMuted}`} />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Session Type Selector */}
              <div className="md:col-span-2">
                <label htmlFor="sessionTypeId" className="form-label">
                  {t("membershipPackageModal.labels.sessionType")}
                </label>
                <select
                  id="sessionTypeId"
                  name="sessionTypeId"
                  value={formData.sessionTypeId}
                  onChange={handleSessionTypeChange}
                  disabled={loadingSessionTypes}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    colors.inputBg
                  } ${colors.inputText} focus:outline-none focus:ring-2 ${
                    colors.inputFocus
                  } focus:border-transparent ${
                    errors.sessionTypeId ? "border-red-300" : colors.inputBorder
                  }`}
                >
                  <option value="">
                    {loadingSessionTypes
                      ? t("membershipPackageModal.options.loading")
                      : t("membershipPackageModal.options.selectType")}
                  </option>
                  {!loadingSessionTypes &&
                    sessionTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}{" "}
                        {type.maxParticipants > 1
                          ? `(Max: ${type.maxParticipants})`
                          : ""}
                      </option>
                    ))}
                </select>
                {errors.sessionTypeId && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.sessionTypeId}
                  </p>
                )}
              </div>

              {/* Package Name */}
              <div className="md:col-span-2">
                <label htmlFor="name" className="form-label">
                  {t("membershipPackageModal.labels.name")}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    colors.inputBg
                  } ${colors.inputText} ${
                    colors.inputPlaceholder
                  } focus:outline-none focus:ring-2 ${
                    colors.inputFocus
                  } focus:border-transparent ${
                    errors.name ? "border-red-300" : colors.inputBorder
                  }`}
                  placeholder={t("membershipPackageModal.placeholders.name")}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Number of Sessions */}
              <div>
                <label htmlFor="numberOfSessions" className="form-label">
                  {t("membershipPackageModal.labels.numberOfSessions")}
                </label>
                <input
                  type="number"
                  id="numberOfSessions"
                  name="numberOfSessions"
                  min="1"
                  value={formData.numberOfSessions}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    colors.inputBg
                  } ${colors.inputText} ${
                    colors.inputPlaceholder
                  } focus:outline-none focus:ring-2 ${
                    colors.inputFocus
                  } focus:border-transparent ${
                    errors.numberOfSessions
                      ? "border-red-300"
                      : colors.inputBorder
                  }`}
                  placeholder={t(
                    "membershipPackageModal.placeholders.sessions",
                  )}
                />
                {errors.numberOfSessions && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.numberOfSessions}
                  </p>
                )}
              </div>

              {/* Price */}
              <div>
                <label htmlFor="price" className="form-label">
                  {t("membershipPackageModal.labels.price")}
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    colors.inputBg
                  } ${colors.inputText} ${
                    colors.inputPlaceholder
                  } focus:outline-none focus:ring-2 ${
                    colors.inputFocus
                  } focus:border-transparent ${
                    errors.price ? "border-red-300" : colors.inputBorder
                  }`}
                  placeholder={t("membershipPackageModal.placeholders.price")}
                />
                {errors.price && (
                  <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                )}
                {formData.price && formData.numberOfSessions && (
                  <p className={`mt-1 text-sm ${colors.textMuted}`}>
                    {t("membershipPackageModal.info.singlePrice", {
                      price: calculateSingleSessionPrice(),
                    })}
                  </p>
                )}
              </div>

              {/* Validity Period */}
              <div>
                <label htmlFor="validityPeriod" className="form-label">
                  {t("membershipPackageModal.labels.validityPeriod")}
                </label>
                <input
                  type="number"
                  id="validityPeriod"
                  name="validityPeriod"
                  min="1"
                  value={formData.validityPeriod}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    colors.inputBg
                  } ${colors.inputText} ${
                    colors.inputPlaceholder
                  } focus:outline-none focus:ring-2 ${
                    colors.inputFocus
                  } focus:border-transparent ${
                    errors.validityPeriod
                      ? "border-red-300"
                      : colors.inputBorder
                  }`}
                  placeholder={t("membershipPackageModal.placeholders.days")}
                />
                {errors.validityPeriod && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.validityPeriod}
                  </p>
                )}
              </div>

              {/* Free Cancellations */}
              <div>
                <label
                  htmlFor="allowedFreeCancellations"
                  className="form-label"
                >
                  {t("membershipPackageModal.labels.freeCancellations")}
                </label>
                <input
                  type="number"
                  id="allowedFreeCancellations"
                  name="allowedFreeCancellations"
                  min="0"
                  value={formData.allowedFreeCancellations}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    colors.inputBg
                  } ${colors.inputText} ${
                    colors.inputPlaceholder
                  } focus:outline-none focus:ring-2 ${
                    colors.inputFocus
                  } focus:border-transparent ${
                    errors.allowedFreeCancellations
                      ? "border-red-300"
                      : colors.inputBorder
                  }`}
                  placeholder="0"
                />
                {errors.allowedFreeCancellations && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.allowedFreeCancellations}
                  </p>
                )}
              </div>

              {/* Cancellation Deadline */}
              <div>
                <label
                  htmlFor="cancellationDeadlineHours"
                  className="form-label"
                >
                  {t("membershipPackageModal.labels.cancellationDeadline")}
                </label>
                <input
                  type="number"
                  id="cancellationDeadlineHours"
                  name="cancellationDeadlineHours"
                  min="0"
                  value={formData.cancellationDeadlineHours}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    colors.inputBg
                  } ${colors.inputText} ${
                    colors.inputPlaceholder
                  } focus:outline-none focus:ring-2 ${
                    colors.inputFocus
                  } focus:border-transparent ${
                    errors.cancellationDeadlineHours
                      ? "border-red-300"
                      : colors.inputBorder
                  }`}
                  placeholder="24"
                />
                <p className={`mt-1 text-xs ${colors.textMuted}`}>
                  {t("membershipPackageModal.info.deadlineDesc")}
                </p>
              </div>

              {/* Late Cancellation Policy */}
              <div>
                <label htmlFor="lateCancellationPolicy" className="form-label">
                  {t("membershipPackageModal.labels.lateCancellation")}
                </label>
                <select
                  id="lateCancellationPolicy"
                  name="lateCancellationPolicy"
                  value={formData.lateCancellationPolicy}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="deduct">
                    {t("membershipPackageModal.options.deduct")}
                  </option>
                  <option value="refund">
                    {t("membershipPackageModal.options.refund")}
                  </option>
                </select>
                <p className={`mt-1 text-xs ${colors.textMuted}`}>
                  {t("membershipPackageModal.info.lateDesc")}
                </p>
              </div>

              {/* No-Show Policy */}
              <div>
                <label htmlFor="noShowPolicy" className="form-label">
                  {t("membershipPackageModal.labels.noShow")}
                </label>
                <select
                  id="noShowPolicy"
                  name="noShowPolicy"
                  value={formData.noShowPolicy}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="deduct">
                    {t("membershipPackageModal.options.deduct")}
                  </option>
                  <option value="refund">
                    {t("membershipPackageModal.options.refund")}
                  </option>
                </select>
                <p className={`mt-1 text-xs ${colors.textMuted}`}>
                  {t("membershipPackageModal.info.noShowDesc")}
                </p>
              </div>

              {/* Status */}
              <div>
                <label htmlFor="status" className="form-label">
                  {t("membershipPackageModal.labels.status")}
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="active">
                    {t("membershipPackageModal.options.active")}
                  </option>
                  <option value="inactive">
                    {t("membershipPackageModal.options.inactive")}
                  </option>
                  <option value="ongoing">
                    {t("membershipPackageModal.options.ongoing")}
                  </option>
                  <option value="completed">
                    {t("membershipPackageModal.options.completed")}
                  </option>
                  <option value="suspended">
                    {t("membershipPackageModal.options.suspended")}
                  </option>
                </select>
              </div>

              {/* Popular Package Checkbox */}
              <div className="md:col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="isPopular"
                    checked={formData.isPopular}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                  />
                  <span
                    className={`text-sm font-medium ${colors.textSecondary}`}
                  >
                    {t("membershipPackageModal.labels.popular")}
                  </span>
                </label>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="form-label">
                {t("membershipPackageModal.labels.description")}
              </label>
              <textarea
                id="description"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleChange}
                className="form-input"
                placeholder={t(
                  "membershipPackageModal.placeholders.description",
                )}
              />
            </div>

            {/* Features */}
            <div>
              <label className="form-label">
                {t("membershipPackageModal.labels.features")}
              </label>
              <div className="space-y-2">
                {/* Add Feature Input */}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    className={`flex-1 px-3 py-2 border rounded-lg ${
                      colors.inputBg
                    } ${colors.inputText} ${
                      colors.inputPlaceholder
                    } focus:outline-none focus:ring-2 ${
                      colors.inputFocus
                    } focus:border-transparent ${colors.inputBorder}`}
                    placeholder={t("membershipPackageModal.placeholders.feature")}
                    onKeyPress={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addFeature())
                    }
                  />
                  <button
                    type="button"
                    onClick={addFeature}
                    className="btn-primary"
                  >
                    {t("membershipPackageModal.buttons.add")}
                  </button>
                </div>

                {/* Features List */}
                {formData.features.length > 0 && (
                  <div className="space-y-1">
                    {formData.features.map((feature, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between ${colors.bgTertiary} px-3 py-2 rounded border ${colors.border}`}
                      >
                        <span className={`text-sm ${colors.textSecondary}`}>
                          {feature}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm transition-colors"
                        >
                          {t("membershipPackageModal.buttons.remove")}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Buttons - Fixed Footer */}
          <div
            className={`flex space-x-3 p-6 border-t ${colors.border} shrink-0`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 border ${colors.btnSecondaryBorder} ${colors.btnSecondaryText} ${colors.btnSecondaryBg} rounded-lg ${colors.btnSecondaryHover} transition-colors`}
            >
              {t("membershipPackageModal.buttons.cancel")}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {loading
                ? t("membershipPackageModal.buttons.saving")
                : membershipPackage
                  ? t("membershipPackageModal.buttons.update")
                  : t("membershipPackageModal.buttons.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MembershipPackageModal;
